import { z } from 'zod';

export type UserAnalyticsEventType =
  | 'UserAnalytics_ExportData'
  | 'UserAnalytics_CreateDashboard'
  | 'UserAnalytics_ApplyDateFilter'
  | 'UserAnalytics_AddTile'
  | 'UserAnalytics_SelectKPIType'
  | 'UserAnalytics_FinishTileCreation'
  | 'UserAnalytics_AddGrouping'
  | 'UserAnalytics_AddFilter'
  | 'UserAnalytics_PageEngagement'
  | 'UserAnalytics_DailyEarningsDateClick'
  | 'UserAnalytics_DateTypeChange'
  | 'UserAnalytics_AnalyticsPageAccess';

// User analytics event payload schemas
export const UserAnalyticsExportDataPayloadSchema = z.object({
  exportFormat: z.string(), // 'csv', 'excel', 'pdf', etc.
  dashboardId: z.string().optional(),
  dashboardName: z.string().optional(),
  dateRange: z.string().optional(),
  filterCount: z.number().optional(),
  supportLevel: z.string().optional(), // The user's support level (e.g., 'A', 'B', 'C', 'D')
});

export const UserAnalyticsCreateDashboardPayloadSchema = z.object({
  dashboardType: z.string().optional(), // 'custom', 'template', etc.
  templateId: z.string().optional(),
  supportLevel: z.string().optional(), // The user's support level (e.g., 'A', 'B', 'C', 'D')
});

export const UserAnalyticsApplyDateFilterPayloadSchema = z.object({
  filterType: z.string(), // 'date_of', 'date_range', 'relative', etc.
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  relativeRange: z.string().optional(), // 'last_7_days', 'last_month', etc.
  dashboardId: z.string().optional(),
  supportLevel: z.string().optional(), // The user's support level (e.g., 'A', 'B', 'C', 'D')
});

export const UserAnalyticsAddTilePayloadSchema = z.object({
  tileType: z.string().optional(), // 'chart', 'kpi', 'table', etc.
  dashboardId: z.string().optional(),
  position: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .optional(),
  supportLevel: z.string().optional(), // The user's support level (e.g., 'A', 'B', 'C', 'D')
});

export const UserAnalyticsSelectKPITypePayloadSchema = z.object({
  kpiType: z.string(), // 'revenue', 'orders', 'conversion', 'users', etc.
  dashboardId: z.string().optional(),
  tileId: z.string().optional(),
  supportLevel: z.string().optional(), // The user's support level (e.g., 'A', 'B', 'C', 'D')
});

export const UserAnalyticsFinishTileCreationPayloadSchema = z.object({
  tileType: z.string(),
  kpiType: z.string().optional(),
  dashboardId: z.string().optional(),
  tileId: z.string(),
  configurationTime: z.number().optional(), // Time spent configuring in seconds
  supportLevel: z.string().optional(), // The user's support level (e.g., 'A', 'B', 'C', 'D')
});

export const UserAnalyticsAddGroupingPayloadSchema = z.object({
  groupingField: z.string(), // 'date', 'product', 'country', etc.
  groupingType: z.string().optional(), // 'daily', 'weekly', 'monthly', etc.
  dashboardId: z.string().optional(),
  tileId: z.string().optional(),
  supportLevel: z.string().optional(), // The user's support level (e.g., 'A', 'B', 'C', 'D')
});

export const UserAnalyticsAddFilterPayloadSchema = z.object({
  filterType: z.string(), // 'product', 'country', 'status', etc.
  filterValue: z.string().optional(),
  filterOperator: z.string().optional(), // 'equals', 'contains', 'greater_than', etc.
  dashboardId: z.string().optional(),
  tileId: z.string().optional(),
  supportLevel: z.string().optional(), // The user's support level (e.g., 'A', 'B', 'C', 'D')
});

export const UserAnalyticsPageEngagementPayloadSchema = z.object({
  pagePath: z.string(), // The page URL path
  timeSpent: z.number(), // Time spent on page in seconds
  engagementThreshold: z.number(), // The threshold that was reached (e.g., 15 seconds)
  userType: z.string().optional(), // 'vendor', 'affiliate', 'admin', etc.
  sessionId: z.string().optional(),
  supportLevel: z.string().optional(), // The user's support level (e.g., 'A', 'B', 'C', 'D')
});

export const UserAnalyticsDailyEarningsDateClickPayloadSchema = z.object({
  date: z.union([z.string(), z.date()]), // The date that was clicked
  earnings: z.number(), // The earnings amount for that date
  currency: z.string(), // The currency code (e.g., 'USD', 'EUR')
  userType: z.string().optional(), // 'vendor', 'affiliate', etc.
  pagePath: z.string(), // The page URL path
  supportLevel: z.string().optional(), // The user's support level (e.g., 'A', 'B', 'C', 'D')
});

export const UserAnalyticsDateTypeChangePayloadSchema = z.object({
  dateType: z.string(), // The selected date type ('promoclick', 'order', 'transaction')
  dashboardId: z.string().optional(), // The dashboard ID
  pagePath: z.string(), // The page URL path
  userType: z.string().optional(), // 'vendor', 'affiliate', 'admin', etc.
  supportLevel: z.string().optional(), // The user's support level (e.g., 'A', 'B', 'C', 'D')
});

export const UserAnalyticsAnalyticsPageAccessPayloadSchema = z.object({
  supportLevel: z.string().optional(), // The user's support level (e.g., 'A', 'B', 'C', 'D')
  userType: z.string().optional(), // 'vendor', 'affiliate', 'admin', etc.
  pagePath: z.string(), // The page URL path
});

export type UserAnalyticsEventTypePayloadMap = {
  UserAnalytics_ExportData: z.infer<typeof UserAnalyticsExportDataPayloadSchema>;
  UserAnalytics_CreateDashboard: z.infer<typeof UserAnalyticsCreateDashboardPayloadSchema>;
  UserAnalytics_ApplyDateFilter: z.infer<typeof UserAnalyticsApplyDateFilterPayloadSchema>;
  UserAnalytics_AddTile: z.infer<typeof UserAnalyticsAddTilePayloadSchema>;
  UserAnalytics_SelectKPIType: z.infer<typeof UserAnalyticsSelectKPITypePayloadSchema>;
  UserAnalytics_FinishTileCreation: z.infer<typeof UserAnalyticsFinishTileCreationPayloadSchema>;
  UserAnalytics_AddGrouping: z.infer<typeof UserAnalyticsAddGroupingPayloadSchema>;
  UserAnalytics_AddFilter: z.infer<typeof UserAnalyticsAddFilterPayloadSchema>;
  UserAnalytics_PageEngagement: z.infer<typeof UserAnalyticsPageEngagementPayloadSchema>;
  UserAnalytics_DailyEarningsDateClick: z.infer<typeof UserAnalyticsDailyEarningsDateClickPayloadSchema>;
  UserAnalytics_DateTypeChange: z.infer<typeof UserAnalyticsDateTypeChangePayloadSchema>;
  UserAnalytics_AnalyticsPageAccess: z.infer<typeof UserAnalyticsAnalyticsPageAccessPayloadSchema>;
};

export const UserAnalyticsEventSchemaMap = {
  UserAnalytics_ExportData: UserAnalyticsExportDataPayloadSchema,
  UserAnalytics_CreateDashboard: UserAnalyticsCreateDashboardPayloadSchema,
  UserAnalytics_ApplyDateFilter: UserAnalyticsApplyDateFilterPayloadSchema,
  UserAnalytics_AddTile: UserAnalyticsAddTilePayloadSchema,
  UserAnalytics_SelectKPIType: UserAnalyticsSelectKPITypePayloadSchema,
  UserAnalytics_FinishTileCreation: UserAnalyticsFinishTileCreationPayloadSchema,
  UserAnalytics_AddGrouping: UserAnalyticsAddGroupingPayloadSchema,
  UserAnalytics_AddFilter: UserAnalyticsAddFilterPayloadSchema,
  UserAnalytics_PageEngagement: UserAnalyticsPageEngagementPayloadSchema,
  UserAnalytics_DailyEarningsDateClick: UserAnalyticsDailyEarningsDateClickPayloadSchema,
  UserAnalytics_DateTypeChange: UserAnalyticsDateTypeChangePayloadSchema,
  UserAnalytics_AnalyticsPageAccess: UserAnalyticsAnalyticsPageAccessPayloadSchema,
};
